/* screens/settings/SecurityTab.jsx — Безопасность (window.SecurityTab).
   Смена пароля (StrengthMeter) · выход со всех устройств (ConfirmDialog) ·
   удаление аккаунта (danger): кодовое слово с КОПИРУЕМОЙ подсказкой (фикс №37),
   кнопка disabled пока не совпало; все подтверждения — ConfirmDialog (не window.confirm).
   Мутации — profileReal (смена пароля / revoke-all / удаление аккаунта с паролем + logout).
   ХЕНДОФФ → frontend/pages/settings/SecurityTab.jsx. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, PasswordInput, StrengthMeter, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { DELETE_CODE_WORD } from '../../lib/enums.js'; // UI-константа кодового слова удаления
import { profileReal } from '../../lib/api/index.js';
import { RR } from '../../lib/router.jsx';

  const { useState } = React;
  const { useNavigate } = RR;
  const CODE = DELETE_CODE_WORD;

  function Card({ title, hint, danger, children }) {
    return (
      <section className={'set-card' + (danger ? ' set-card--danger-zone' : '')}>
        <div className="set-card__head"><h3 className="t-h4">{title}</h3>{hint && <span className="t-caption text-mute">{hint}</span>}</div>
        <div className="set-card__body">{children}</div>
      </section>
    );
  }

  function SecurityTab() {
    const toast = useToast();
    const confirm = useConfirm();
    const navigate = useNavigate();
    const { logout } = useAuth();

    const [cur, setCur] = useState('');
    const [next, setNext] = useState('');
    const [repeat, setRepeat] = useState('');
    const [pwErr, setPwErr] = useState(null);
    const [pwBusy, setPwBusy] = useState(false);

    const [showDelete, setShowDelete] = useState(false);
    const [delPw, setDelPw] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [codeCopied, setCodeCopied] = useState(false);

    const canChange = cur && next && next === repeat && next.length >= 8;

    const changePw = async () => {
      setPwErr(null); setPwBusy(true);
      try {
        await profileReal.changePassword({ current_password: cur, new_password: next });
        setCur(''); setNext(''); setRepeat('');
        toast.show('Пароль изменён', 'success');
      } catch (e) { setPwErr(e.code === 'invalid_password' ? 'Текущий пароль неверен' : (e.code === 'rate_limited' ? 'Слишком часто, попробуйте позже' : 'Не удалось сменить пароль')); }
      finally { setPwBusy(false); }
    };

    const revokeAll = async () => {
      const ok = await confirm({ title: 'Выйти со всех устройств?', body: 'Все активные сессии, кроме текущей, будут завершены. Потребуется повторный вход.', confirmLabel: 'Выйти со всех', cancelLabel: 'Отмена', tone: 'danger' });
      if (!ok) return;
      try { const r = await profileReal.revokeOtherSessions(); toast.show('Завершено сессий: ' + (r.revoked || 0), 'success'); }
      catch (e) { toast.show('Не удалось завершить сессии', 'danger'); }
    };

    const copyCode = () => {
      try { if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(CODE); } catch (e) {}
      setCodeCopied(true); setTimeout(() => setCodeCopied(false), 1800);
    };

    const doDelete = async () => {
      const ok = await confirm({ title: 'Удалить аккаунт безвозвратно?', body: 'Все ваши данные и доступ к пространствам будут удалены без возможности восстановления.', confirmLabel: 'Удалить аккаунт', cancelLabel: 'Отмена', tone: 'danger' });
      if (!ok) return;
      try {
        await profileReal.deleteAccount({ password: delPw });
        // Бэк сносит все сессии (включая текущую); чистим клиентское состояние и уводим на вход.
        toast.show('Аккаунт удалён', 'muted');
        await logout();
        navigate('/login');
      } catch (e) { toast.show(e.code === 'invalid_password' ? 'Неверный пароль' : 'Не удалось удалить аккаунт', 'danger'); }
    };

    return (
      <div className="settings-panel stack stack-16">
        <Card title="Смена пароля">
          <div className="pw-form">
            <Field label="Текущий пароль" error={pwErr}>
              <PasswordInput value={cur} invalid={!!pwErr} onChange={(e) => { setCur(e.target.value); setPwErr(null); }} placeholder="••••••••" />
            </Field>
            <Field label="Новый пароль" hint="Минимум 8 символов">
              <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} placeholder="••••••••" />
              <div className="pw-form__strength"><StrengthMeter value={next} /></div>
            </Field>
            <Field label="Повторите новый пароль" error={repeat && next !== repeat ? 'Пароли не совпадают' : null}>
              <PasswordInput value={repeat} invalid={!!(repeat && next !== repeat)} onChange={(e) => setRepeat(e.target.value)} placeholder="••••••••" />
            </Field>
            <div className="cluster">
              <Button variant="primary" iconLeft="lock" disabled={!canChange || pwBusy} loading={pwBusy} onClick={changePw}>Сменить пароль</Button>
            </div>
          </div>
        </Card>

        <Card title="Активные сессии" hint="Управление входами на других устройствах">
          <div className="set-card__danger">
            <div className="stack stack-2">
              <span className="t-label">Выйти со всех устройств</span>
              <span className="t-caption text-dim">Завершит все сессии, кроме текущей.</span>
            </div>
            <Button variant="secondary" size="sm" iconLeft="logout" onClick={revokeAll}>Выйти со всех</Button>
          </div>
        </Card>

        <Card title="Удаление аккаунта" danger>
          {!showDelete ? (
            <div className="set-card__danger">
              <div className="stack stack-2">
                <span className="t-label">Удалить аккаунт</span>
                <span className="t-caption text-dim">Действие необратимо. Все данные будут удалены.</span>
              </div>
              <Button variant="danger" size="sm" iconLeft="trash" onClick={() => setShowDelete(true)}>Удалить аккаунт</Button>
            </div>
          ) : (
            <div className="delete-form stack stack-12">
              <div className="callout callout--bad">
                <Icon name="alert-triangle" size={16} />
                <div className="t-caption">Это действие невозможно отменить. Аккаунт и все пространства, где вы единственный владелец, будут удалены.</div>
              </div>
              <Field label="Текущий пароль">
                <PasswordInput value={delPw} onChange={(e) => setDelPw(e.target.value)} placeholder="••••••••" />
              </Field>
              <Field label="Кодовое слово" hint="Введите слово ниже, чтобы подтвердить">
                <div className="code-hint">
                  <code className="code-hint__word num">{CODE}</code>
                  <button type="button" className="code-hint__copy" onClick={copyCode} aria-label="Скопировать кодовое слово">
                    <Icon name={codeCopied ? 'check' : 'copy'} size={14} />{codeCopied ? 'Скопировано' : 'Скопировать'}
                  </button>
                </div>
                <TextInput value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={CODE} invalid={!!confirmText && confirmText !== CODE} />
              </Field>
              <div className="cluster cluster-8">
                <Button variant="ghost" size="sm" onClick={() => { setShowDelete(false); setConfirmText(''); setDelPw(''); }}>Отмена</Button>
                <Button variant="danger" size="sm" iconLeft="trash" disabled={confirmText !== CODE || !delPw} onClick={doDelete}>Удалить аккаунт навсегда</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    );
  }

export { SecurityTab };
